import React, { useState, useEffect, forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { supabase } from '../../../supabaseClient';
import { getOrgId } from '../../../services/orgStore';
import { apiUrl } from '../../../utils/apiBase';
import { quickBooksAuthHeaders } from '../../../utils/quickBooksAuth';
import { useQuickBooksStatus } from '../../../context/QuickBooksStatusContext';
import { useXeroStatus } from '../../../context/XeroStatusContext';
import SearchableSupplierSelect from './SearchableSupplierSelect';
import SearchableItemSelect from '../invoices/SearchableItemSelect';
import SimpleDropdown from '../invoices/SimpleDropdown';
import {
  calculateLineTotal,
  calculateLineTax,
  calculateBillTotals,
  getTaxRate,
} from './billCalculations';

/* ---- Date helpers ---- */
const parseYmd = (ymd) => {
  if (!ymd) return null;
  const d = new Date(`${ymd}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatYmd = (date) => {
  if (!date || Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
  </svg>
  );

const DateInput = forwardRef(({ value, onClick, placeholder }, ref) => (
  <div className="relative">
    <input
      ref={ref}
      value={value || ''}
      onClick={onClick}
      readOnly
      placeholder={placeholder}
      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded cursor-pointer font-[Arial] focus:outline-none focus:ring-1 focus:ring-blue-500"
    />
    <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
      <CalendarIcon />
    </span>
  </div>
));

const PopperContainer = ({ children }) => <div className="z-[11000]">{children}</div>;

/* ---- Defaults ---- */
const emptyLine = {
  item_id: '', item_description: '', quantity: 1,
  unit_price: 0, total_price: 0, tax_rate_id: 'GST', gst_rate: 0.1,
};

const defaultFormData = () => ({
  supplier_id: '',
  supplier_name: '',
  bill_number: '',
  reference: '',
  bill_date: formatYmd(new Date()),
  due_date: '',
  tax_inclusion: 'tax_exclusive',
  note_to_supplier: '', note_for_bill_hidden: '',
});

/* ================================================================ */

export default function BillModal({
  isOpen, onClose, onSubmit, initialData,
  draftToRestore, onClearDraftRestore,
  suppliers = [], items = [], taxes = [],
  setup = [], setup2 = [], onPreview,
  bills = [],
  onCreateSupplier,
  createdSupplierId,
  onQuickBooksSynced,
  onXeroSynced,
}) {
  const [formData, setFormData] = useState(defaultFormData());
  const [lineItems, setLineItems] = useState([{ ...emptyLine, id: Date.now() }]);
  const [qbSending, setQbSending] = useState(false);
  const { loading: qbStatusLoading, connected: qbConnected } = useQuickBooksStatus();
  const showQuickBooksSend = !qbStatusLoading && qbConnected;
  const [xeroSending, setXeroSending] = useState(false);
  const { loading: xeroStatusLoading, connected: xeroConnected } = useXeroStatus();
  const showXeroSend = !xeroStatusLoading && xeroConnected;

  /* ---- Initialise / load ---- */
  useEffect(() => {
    if (!isOpen) return;

    if (draftToRestore) {
      setFormData(draftToRestore.formData);
      setLineItems(draftToRestore.lineItems || [{ ...emptyLine, id: Date.now() }]);
      onClearDraftRestore?.();
      return;
    }

    if (initialData) {
      setFormData({
        supplier_id: initialData.supplier_id || '',
        supplier_name: initialData.supplier_name || initialData.supplier?.supplier_name || '',
        bill_number: initialData.bill_number || '',
        reference: initialData.reference || '',
        bill_date: initialData.bill_date?.split('T')[0] || formatYmd(new Date()),
        due_date: initialData.due_date?.split('T')[0] || '',
        tax_inclusion: (() => {
          const t = initialData.line_amount_types;
          if (t === 'inclusive') return 'tax_inclusive';
          if (t === 'exclusive') return 'tax_exclusive';
          if (t === 'none') return 'no_tax';
          return 'tax_exclusive';
        })(),
        note_to_supplier: initialData.note_to_supplier || '',
        note_for_bill_hidden: initialData.note_for_bill_hidden || '',
      });

      (async () => {
        const { data, error } = await supabase
          .from('bill_line_items')
          .select('*')
          .eq('bill_id', initialData.bill_id)
          .order('line_number', { ascending: true });
        if (!error && data?.length) {
          setLineItems(data.map((l) => ({
            id: l.bill_line_id || Date.now() + Math.random(),
            item_id: l.item_id || '',
            item_description: l.item_description || '',
            quantity: l.qty || 1,
            unit_price: l.unit_price || 0,
            total_price: l.line_total_inclusive_tax || 0,
            tax_rate_id: l.tax_id || 'GST',
            gst_rate: 0.1,
          })));
        } else {
          setLineItems([{ ...emptyLine, id: Date.now() }]);
        }
      })();
    } else {
      setFormData(defaultFormData());
      setLineItems([{ ...emptyLine, id: Date.now() }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialData]);

  /* ---- Handlers ---- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === 'tax_inclusion') {
      setLineItems((prev) =>
        prev.map((item) => {
          const rate = getTaxRate(item.tax_rate_id || 'GST', taxes);
          return { ...item, total_price: calculateLineTotal(item.quantity || 0, item.unit_price || 0, rate, value) };
        })
      );
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    if (!createdSupplierId) return;
    // Only auto-fill right after creating a supplier for a NEW bill.
    // Never override supplier when editing an existing bill.
    if (initialData) return;
    const s = suppliers.find((x) => x.supplier_id?.toString() === createdSupplierId?.toString());
    if (!s) return;
    setFormData((prev) => ({
      ...prev,
      supplier_id: s.supplier_id?.toString?.() ?? '',
      supplier_name: s.supplier_name || '',
    }));
  }, [isOpen, createdSupplierId, suppliers, initialData]);

  const handleLineChange = (id, field, val, selectedProduct = null) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const updated = { ...item, [field]: val };

        if (field === 'item_id') {
          const product = selectedProduct || items.find((i) => i.item_id === val);
          if (product) {
            updated.item_id = val;
            updated.item_description = product.sales_description || '';
            updated.unit_price =
              formData.tax_inclusion === 'tax_inclusive'
                ? product.selling_price_incl || 0
                : product.selling_price_excl || product.selling_price_incl || 0;
            if (product.sales_tax_code) updated.tax_rate_id = product.sales_tax_code;
          }
        }

        const qty = field === 'quantity' ? parseFloat(val) || 0 : updated.quantity || 1;
        const price = field === 'unit_price' ? parseFloat(val) || 0 : updated.unit_price;
        const rate = getTaxRate(field === 'tax_rate_id' ? val : updated.tax_rate_id || 'GST', taxes);
        updated.total_price = calculateLineTotal(qty, price, rate, formData.tax_inclusion);

        return updated;
      })
    );
  };

  const addLine = () => setLineItems([...lineItems, { ...emptyLine, id: Date.now() }]);
  const removeLine = (id) => { if (lineItems.length > 1) setLineItems(lineItems.filter((l) => l.id !== id)); };
  const clearLines = () => setLineItems([{ ...emptyLine, id: Date.now() }]);

  const { subtotal, taxTotal, grandTotal } = calculateBillTotals(lineItems, taxes, formData.tax_inclusion);

  /* ---- Build draft object (shared by preview) ---- */
  const buildDraft = () => {
    const supplier = suppliers.find((s) => s.supplier_id?.toString() === formData.supplier_id?.toString());
    return {
      formData: {
        ...formData,
        ...(initialData?.bill_id != null ? { bill_id: initialData.bill_id } : {}),
        supplier_name: supplier?.supplier_name || '',
      },
      company: Array.isArray(setup) ? setup[0] : setup,
      lineItems, subtotal, taxTotal, grandTotal, taxes, setup2,
    };
  };

  const handlePreview = () => onPreview?.(buildDraft());

  const handleExportToQuickBooks = async () => {
    const billId = initialData?.bill_id;
    if (!billId) return;
    const orgId = getOrgId();
    if (!orgId) {
      alert('No organisation selected. Try signing in again.');
      return;
    }
    if (initialData?.quickbooks_bill_id) {
      alert(`This bill is already in QuickBooks (Id: ${initialData.quickbooks_bill_id}).`);
      return;
    }
    setQbSending(true);
    try {
      const auth = await quickBooksAuthHeaders();
      if (!auth.Authorization) {
        alert('You must be signed in to send bills to QuickBooks.');
        return;
      }
      const res = await fetch(apiUrl('/api/quickbooks/export-bill'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        credentials: 'include',
        body: JSON.stringify({ bill_id: billId, org_id: orgId }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) {
        alert('Connect QuickBooks first in Settings, then try again.');
        return;
      }
      if (res.status === 409) {
        alert(data.error || 'Already exported.');
        onQuickBooksSynced?.({
          bill_id: billId,
          quickbooks_bill_id: data.quickbooks_bill_id,
          quickbooks_synced_at: data.quickbooks_synced_at,
        });
        return;
      }
      if (!res.ok) throw new Error(data.error || 'Export failed');
      alert(`Bill created in QuickBooks (Id: ${data.quickbooks_bill_id}).`);
      onQuickBooksSynced?.({
        bill_id: billId,
        quickbooks_bill_id: data.quickbooks_bill_id,
        quickbooks_synced_at: data.quickbooks_synced_at || new Date().toISOString(),
      });
    } catch (err) {
      alert(err.message || 'Failed to send to QuickBooks.');
    } finally {
      setQbSending(false);
    }
  };

  const handleExportToXero = async () => {
    const billId = initialData?.bill_id;
    if (!billId) return;
    const orgId = getOrgId();
    if (!orgId) {
      alert('No organisation selected. Try signing in again.');
      return;
    }
    if (initialData?.xero_bill_id) {
      alert(`This bill is already in Xero (Id: ${initialData.xero_bill_id}).`);
      return;
    }
    setXeroSending(true);
    try {
      const auth = await quickBooksAuthHeaders();
      if (!auth.Authorization) {
        alert('You must be signed in to send bills to Xero.');
        return;
      }
      const res = await fetch(apiUrl('/api/xero/export-bill'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        credentials: 'include',
        body: JSON.stringify({ bill_id: billId, org_id: orgId }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) {
        alert('Connect Xero first in Settings, then try again.');
        return;
      }
      if (res.status === 409) {
        alert(data.error || 'Already exported.');
        onXeroSynced?.({
          bill_id: billId,
          xero_bill_id: data.xero_bill_id,
          xero_synced_at: data.xero_synced_at,
        });
        return;
      }
      if (!res.ok) throw new Error(data.error || 'Export failed');
      alert(`Bill ${data.invoice_number || ''} created in Xero.`.replace('  ', ' '));
      onXeroSynced?.({
        bill_id: billId,
        xero_bill_id: data.xero_bill_id,
        xero_synced_at: data.xero_synced_at || new Date().toISOString(),
      });
    } catch (err) {
      alert(err.message || 'Failed to send to Xero.');
    } finally {
      setXeroSending(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.bill_number.trim()) {
      alert('Invoice number is required.');
      return;
    }
    const editingId = initialData?.bill_id;
    const duplicate = bills.find(
      (b) => b.bill_number === formData.bill_number.trim() && b.bill_id !== editingId
    );
    if (duplicate) {
      alert(`Invoice number "${formData.bill_number.trim()}" is already used by an existing bill.`);
      return;
    }

    const { tax_inclusion, ...dbData } = formData;
    const lineAmountTypes = tax_inclusion === 'tax_inclusive' ? 'inclusive' : tax_inclusion === 'tax_exclusive' ? 'exclusive' : 'none';

    onSubmit({
      header: { ...dbData, line_amount_types: lineAmountTypes, total_exclusive_tax: subtotal, tax_amount: taxTotal, total_amount: grandTotal, is_closed: false, is_void: false },
      lines: lineItems.map(({ id, ...rest }) => {
        const selectedTax = taxes.find((t) => t.tax_id === rest.tax_rate_id);
        return {
          ...rest,
          line_tax_amount: calculateLineTax(rest, taxes, tax_inclusion),
          tax_rate: selectedTax ? parseFloat(selectedTax.tax_rate) : null,
        };
      }),
    });
  };

  if (!isOpen) return null;

  /* ================================================================ */
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]">
      <div className="bg-white w-[90%] max-w-[1500px] h-[95vh] rounded-lg flex flex-col overflow-hidden shadow-xl">

        {/* Header */}
        <div className="px-8 py-5 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold font-[Arial]">
            {initialData ? 'Edit Bill' : 'Create Bill'}
          </h2>
          <button onClick={onClose} className="text-2xl text-gray-400 bg-transparent border-none cursor-pointer hover:text-gray-600">×</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-x-auto overflow-y-auto p-8 bg-gray-50">
          <form id="billForm" onSubmit={handleSubmit}
            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}>

            {/* Header Fields */}
            <div className="mb-10 font-[Arial]">
              <div className="grid grid-cols-[210px_200px_150px_150px_150px_1fr] gap-4 mb-4 items-end">
                <div>
                  <label className="block text-[13px] text-gray-700 font-medium mb-1">From (Supplier)</label>
                  <SearchableSupplierSelect
                    value={formData.supplier_id}
                    suppliers={suppliers}
                    onSelect={(s) => {
                      setFormData((prev) => ({
                        ...prev,
                        supplier_id: s.supplier_id.toString(),
                        supplier_name: s.supplier_name || '',
                      }));
                    }}
                    onCreateSupplier={onCreateSupplier}
                  />
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 font-medium mb-1">Supplier ID</label>
                  <input type="text" name="supplier_name" value={formData.supplier_id} onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 font-medium mb-1">Bill date</label>
                  <DatePicker selected={parseYmd(formData.bill_date)} onChange={(d) => handleChange({ target: { name: 'bill_date', value: formatYmd(d) } })}
                    dateFormat="d MMM yyyy" placeholderText="Select date" showPopperArrow={false} popperContainer={PopperContainer} customInput={<DateInput />}
                    showMonthDropdown showYearDropdown scrollableYearDropdown yearDropdownItemNumber={10} calendarStartDay={1} popperClassName="custom-datepicker" />
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 font-medium mb-1">Due date</label>
                  <DatePicker selected={parseYmd(formData.due_date)} onChange={(d) => handleChange({ target: { name: 'due_date', value: formatYmd(d) } })}
                    dateFormat="d MMM yyyy" placeholderText="Select date" showPopperArrow={false} popperContainer={PopperContainer} customInput={<DateInput />}
                    showMonthDropdown showYearDropdown scrollableYearDropdown yearDropdownItemNumber={10} calendarStartDay={1} popperClassName="custom-datepicker" />
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 font-medium mb-1">Reference</label>
                  <input type="text" name="reference" value={formData.reference} onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div className="flex gap-2.5 items-stretch">
                  <div className="flex-1 min-w-0">
                    <label className="block text-[13px] text-gray-700 font-medium mb-1">Bill no. <span className="text-red-500">*</span></label>
                    <input type="text" name="bill_number" value={formData.bill_number} onChange={handleChange} placeholder="Enter supplier invoice no."
                      readOnly={!!initialData}
                      className={`w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${initialData ? 'bg-gray-100 cursor-not-allowed' : ''}`} />
                  </div>
                  <button type="button" onClick={handlePreview}
                    className="self-end mb-px px-4 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-md bg-white cursor-pointer hover:bg-blue-50 transition-colors">
                    Preview
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-[200px] gap-4 mb-2.5">
                <div>
                  <label className="block text-[13px] text-gray-700 font-medium mb-1">Amounts are</label>
                  <SimpleDropdown value={formData.tax_inclusion || 'tax_exclusive'}
                    options={[{ value: 'tax_inclusive', label: 'Tax inclusive' }, { value: 'tax_exclusive', label: 'Tax exclusive' }, { value: 'no_tax', label: 'No tax' }]}
                    onSelect={(v) => handleChange({ target: { name: 'tax_inclusion', value: v } })} />
                </div>
              </div>
            </div>

            {/* Line Items Table */}
            <div className="bg-white rounded-lg border border-gray-200 mb-8 overflow-hidden font-[Arial]">
              <table className="w-full border-collapse">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-2.5 text-xs font-semibold text-gray-700 border-b border-gray-200 text-left w-10">#</th>
                    <th className="p-2.5 text-xs font-semibold text-gray-700 border-b border-gray-200 text-left w-60">Item</th>
                    <th className="p-2.5 text-xs font-semibold text-gray-700 border-b border-gray-200 text-left">Description</th>
                    <th className="p-2.5 text-xs font-semibold text-gray-700 border-b border-gray-200 text-right w-20">Qty</th>
                    <th className="p-2.5 text-xs font-semibold text-gray-700 border-b border-gray-200 text-right w-28">Unit Price</th>
                    <th className="p-2.5 text-xs font-semibold text-gray-700 border-b border-gray-200 text-left w-28">Tax Rate</th>
                    <th className="p-2.5 text-xs font-semibold text-gray-700 border-b border-gray-200 text-right w-28">Tax Amount</th>
                    <th className="p-2.5 text-xs font-semibold text-gray-700 border-b border-gray-200 text-right w-28">Amount</th>
                    <th className="p-2.5 text-xs font-semibold text-gray-700 border-b border-gray-200 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((item, idx) => (
                    <tr key={item.id}>
                      <td className="p-2 border-b border-gray-100 text-center text-gray-500">{idx + 1}</td>
                      <td className="p-2 border-b border-gray-100">
                        <SearchableItemSelect value={item.item_id || ''} items={items}
                          onSelect={(sel) => handleLineChange(item.id, 'item_id', sel.item_id, sel)} />
                      </td>
                      <td className="p-2 border-b border-gray-100">
                        <input type="text" value={item.item_description || ''} onChange={(e) => handleLineChange(item.id, 'item_description', e.target.value)}
                          className="w-full px-1 py-1 text-sm border-none focus:outline-none" />
                      </td>
                      <td className="p-2 border-b border-gray-100">
                        <input type="number" min="1" value={item.quantity} onChange={(e) => handleLineChange(item.id, 'quantity', e.target.value)}
                          className="w-full px-1 py-1 text-sm text-right border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500" />
                      </td>
                      <td className="p-2 border-b border-gray-100">
                        <input type="number" min="0" step="0.01" value={item.unit_price} onChange={(e) => handleLineChange(item.id, 'unit_price', e.target.value)}
                          className="w-full px-1 py-1 text-sm text-right border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500" />
                      </td>
                      <td className="p-2 border-b border-gray-100">
                        <SimpleDropdown value={item.tax_rate_id || ''}
                          options={[{ value: '', label: 'No tax' }, ...taxes.map((t) => ({ value: t.tax_id, label: `${t.tax_id} (${t.tax_rate}%)` }))]}
                          onSelect={(v) => handleLineChange(item.id, 'tax_rate_id', v)} />
                      </td>
                      <td className="p-2 border-b border-gray-100 text-right text-gray-500">
                        {formData.tax_inclusion !== 'no_tax' && item.tax_rate_id
                          ? calculateLineTax(item, taxes, formData.tax_inclusion).toFixed(2) : ''}
                      </td>
                      <td className="p-2 border-b border-gray-100 text-right font-medium">{(item.total_price || 0).toFixed(2)}</td>
                      <td className="p-2 border-b border-gray-100 text-center">
                        <button type="button" onClick={() => removeLine(item.id)}
                          className="bg-transparent border-none cursor-pointer text-red-500 hover:text-red-700" title="Remove line">🗑️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-3 flex gap-2.5 border-t border-gray-200">
                <button type="button" onClick={addLine} className="px-3 py-1.5 text-sm font-medium bg-gray-100 border border-gray-300 rounded cursor-pointer hover:bg-gray-200 transition-colors">Add line item</button>
                <button type="button" onClick={clearLines} className="px-3 py-1.5 text-sm font-medium bg-white border border-gray-300 rounded cursor-pointer hover:bg-gray-50 transition-colors">Clear all lines</button>
              </div>
            </div>

            {/* Footer Summary */}
            <div className="grid grid-cols-[1fr_300px] gap-10 font-[Arial]">
              <div className="flex flex-col gap-5">
                <div>
                  <label className="block text-[13px] text-gray-700 font-medium mb-1">Note to supplier</label>
                  <textarea name="note_to_supplier" value={formData.note_to_supplier} onChange={handleChange}
                    className="w-full max-w-[500px] min-h-[80px] px-3 py-2 text-sm border border-gray-300 rounded font-[Arial] focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-[13px] text-gray-700 font-medium mb-1">Memo on statement (hidden)</label>
                  <textarea name="note_for_bill_hidden" value={formData.note_for_bill_hidden} onChange={handleChange}
                    className="w-full max-w-[500px] min-h-[60px] px-3 py-2 text-sm border border-gray-300 rounded font-[Arial] focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
              </div>
              <div className="flex flex-col gap-3 items-end pt-5">
                <div className="flex justify-between w-full text-sm"><span className="text-gray-500">Subtotal</span><span className="font-medium">{subtotal.toFixed(2)}</span></div>
                {formData.tax_inclusion !== 'no_tax' && taxTotal > 0 && (
                  <div className="flex justify-between w-full text-sm"><span className="text-gray-500">Total GST on Expenses</span><span className="font-medium">{taxTotal.toFixed(2)}</span></div>
                )}
                <div className="w-full h-px bg-gray-200 my-2.5" />
                <div className="flex justify-between w-full text-lg font-bold"><span>Total</span><span>{grandTotal.toFixed(2)}</span></div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="px-8 py-4 border-t border-gray-200 bg-white flex flex-wrap items-center gap-2.5">
          {initialData?.bill_id && showQuickBooksSend && (
            <button
              type="button"
              onClick={handleExportToQuickBooks}
              disabled={qbSending || !!initialData?.quickbooks_bill_id}
              className={`px-6 py-2.5 rounded-md font-semibold text-sm transition-colors ${
                initialData?.quickbooks_bill_id || qbSending
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-[#2ca01c] text-white cursor-pointer hover:opacity-90'
              }`}
              title={
                initialData?.quickbooks_bill_id
                  ? 'Already sent to QuickBooks'
                  : 'Create this bill in QuickBooks Online'
              }
            >
              {qbSending ? 'QuickBooks…' : initialData?.quickbooks_bill_id ? 'In QuickBooks' : 'Send to QuickBooks'}
            </button>
          )}
          {initialData?.bill_id && showXeroSend && (
            <button
              type="button"
              onClick={handleExportToXero}
              disabled={xeroSending || !!initialData?.xero_bill_id}
              className={`px-6 py-2.5 rounded-md font-semibold text-sm transition-colors ${
                initialData?.xero_bill_id || xeroSending
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-[#13B5EA] text-white cursor-pointer hover:opacity-90'
              }`}
              title={
                initialData?.xero_bill_id
                  ? 'Already sent to Xero'
                  : 'Create this bill in Xero'
              }
            >
              {xeroSending ? 'Xero…' : initialData?.xero_bill_id ? 'In Xero' : 'Send to Xero'}
            </button>
          )}
          <div className="flex-1 min-w-[8rem]" />
          <button type="submit" form="billForm" className="px-6 py-2.5 bg-emerald-500 text-white rounded-md font-semibold text-sm cursor-pointer hover:bg-emerald-600 transition-colors">Save</button>
          <button type="button" onClick={onClose} className="px-5 py-2.5 bg-white border border-gray-300 rounded-md font-medium text-sm cursor-pointer hover:bg-gray-50 transition-colors">Cancel</button>
        </div>
      </div>
    </div>
  );
}
